import axios from 'axios';

type DownloadProgress = {
  progress: number;
  speed: number;
  timeLeft: number;
};

const MAX_RETRIES = 3;

function waitUntilVisible(signal: AbortSignal): Promise<void> {
  if (document.visibilityState === 'visible') return Promise.resolve();

  return new Promise((resolve) => {
    const onChange = () => {
      if (document.visibilityState !== 'visible' && !signal.aborted) return;
      document.removeEventListener('visibilitychange', onChange);
      signal.removeEventListener('abort', onChange);
      resolve();
    };
    document.addEventListener('visibilitychange', onChange);
    signal.addEventListener('abort', onChange);
  });
}

export async function downloadFile(
  url: string,
  fileName: string,
  abortController: AbortController,
  onProgress?: (data: DownloadProgress) => void,
): Promise<File> {
  for (let attempt = 0; ; attempt++) {
    try {
      const startTime = Date.now();

      const response = await axios.get(url, {
        signal: abortController.signal,
        responseType: 'blob',

        onDownloadProgress: (event) => {
          if (event.total && onProgress) {
            const now = Date.now();
            const progress = Math.round((event.loaded * 100) / event.total);
            const timeUploading = (now - startTime) / 1000;
            const speed = event.loaded / timeUploading;
            const timeLeft = (event.total - event.loaded) / speed;

            onProgress({
              progress,
              speed,
              timeLeft,
            });
          }
        },
      });

      const file = new File([response.data], fileName, {
        type: response.data.type,
      });

      return file;
    } catch (error) {
      const isNetworkError = axios.isAxiosError(error) && !error.response && !axios.isCancel(error);

      if (!isNetworkError || attempt >= MAX_RETRIES) {
        console.error('Error downloading file:', error);
        throw error;
      }

      await waitUntilVisible(abortController.signal);
    }
  }
}
